/**
 * KeyItem entity: a collectible key that appears in the maze.
 *
 * Keys come in three tiers (metallic, copper, gold) and are used
 * to open matching-tier chests. The player can carry up to 3 keys.
 *
 * Features:
 *  - Pop-in scale animation on spawn
 *  - Gentle float/bob animation
 *  - Subtle glow pulse matching the key's tier color
 *  - Collect animation with label text
 *  - Keys do NOT despawn on their own (persist until collected)
 */

import Phaser from 'phaser';
import { KEY_TEXTURE_MAP, KeyTier } from '../utils/constants.js';
import {
  AUDIO_KEY_COLLECT,
  COLOR_METALLIC,
  COLOR_COPPER,
  COLOR_GOLD,
} from '../config/game-config.js';
import { getSfxVol } from '../utils/audio-config.js';

/** @type {Object<string, number>} Tier to tint color mapping */
const TIER_GLOW_COLOR = Object.freeze({
  [KeyTier.METALLIC]: COLOR_METALLIC,
  [KeyTier.COPPER]: COLOR_COPPER,
  [KeyTier.GOLD]: COLOR_GOLD,
});

/** @type {Object<string, string>} Tier to display name mapping */
const TIER_NAME = Object.freeze({
  [KeyTier.METALLIC]: 'Metallic Key',
  [KeyTier.COPPER]: 'Copper Key',
  [KeyTier.GOLD]: 'Gold Key',
});

export default class KeyItem extends Phaser.GameObjects.Sprite {
  /**
   * @param {Phaser.Scene} scene
   * @param {number} x - World X position (center of cell)
   * @param {number} y - World Y position (center of cell)
   * @param {string} tier - One of KeyTier values
   * @param {number} gridCol - Grid column where the key sits
   * @param {number} gridRow - Grid row where the key sits
   */
  constructor(scene, x, y, tier, gridCol, gridRow) {
    const texKey = KEY_TEXTURE_MAP[tier] || KEY_TEXTURE_MAP[KeyTier.METALLIC];
    super(scene, x, y, texKey);

    /** @type {string} */
    this.tier = tier;

    /** @type {number} Grid column */
    this.gridCol = gridCol;
    /** @type {number} Grid row */
    this.gridRow = gridRow;

    /** @type {boolean} Whether this key has been collected (prevents double-collect). */
    this._collected = false;

    scene.add.existing(this);
    this.setDepth(5);

    // Note: physics body is created automatically by the PhysicsGroup
    // when this item is added via keyGroup.add(). Do NOT call
    // scene.physics.add.existing() here — it conflicts with the group.

    // --- Pop-in scale animation ---
    this.setScale(0);
    scene.tweens.add({
      targets: this,
      scaleX: 1,
      scaleY: 1,
      duration: 300,
      ease: 'Back.easeOut',
    });

    // --- Gentle float animation ---
    this._floatTween = scene.tweens.add({
      targets: this,
      y: y - 2,
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // --- Glow effect ---
    this._glow = scene.add.graphics();
    this._glow.setDepth(4);
    this._drawGlow(x, y);
    this._glowTween = scene.tweens.add({
      targets: this._glow,
      alpha: { from: 0.6, to: 0.2 },
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * Draw a subtle glow circle behind the key.
   * @private
   * @param {number} x
   * @param {number} y
   */
  _drawGlow(x, y) {
    if (!this._glow) return;
    this._glow.clear();
    const color = TIER_GLOW_COLOR[this.tier] || COLOR_METALLIC;
    this._glow.fillStyle(color, 0.15);
    this._glow.fillCircle(x, y, 8);
    this._glow.fillStyle(color, 0.08);
    this._glow.fillCircle(x, y, 12);
  }

  /**
   * Collect this key — plays animation, sound, and label, then destroys.
   * Called by GameScene when the player overlaps this key.
   */
  collect() {
    if (this._collected) return;
    this._collected = true;
    this.active = false;

    // Stop ongoing animations
    if (this._floatTween) {
      this._floatTween.destroy();
      this._floatTween = null;
    }
    if (this._glowTween) {
      this._glowTween.destroy();
      this._glowTween = null;
    }

    // Play collect sound (gracefully handle missing audio file)
    try {
      this.scene.sound.play(AUDIO_KEY_COLLECT, { volume: getSfxVol(0.5) });
    } catch (_) { /* audio file may not exist yet */ }

    // Show tier name label
    this._showCollectLabel();

    // Scale + fade collect animation
    this.scene.tweens.add({
      targets: this,
      scaleX: 1.6,
      scaleY: 1.6,
      alpha: 0,
      duration: 350,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.destroy();
      },
    });

    // Fade out glow
    if (this._glow) {
      this.scene.tweens.add({
        targets: this._glow,
        alpha: 0,
        duration: 250,
        onComplete: () => {
          if (this._glow) {
            this._glow.destroy();
            this._glow = null;
          }
        },
      });
    }
  }

  /**
   * Display a floating label showing the key tier name.
   * @private
   */
  _showCollectLabel() {
    if (!this.scene) return;

    // Capture scene reference — this.scene will be null after destroy
    const scene = this.scene;

    const name = TIER_NAME[this.tier] || 'Key';
    const color = TIER_GLOW_COLOR[this.tier] || 0xffffff;
    const hexStr = '#' + color.toString(16).padStart(6, '0');

    const label = scene.add.text(this.x, this.y - 10, name, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '10px',
      fontStyle: 'bold',
      color: hexStr,
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5).setDepth(150);

    // Zoom in from small, hold, then float up and fade out
    label.setScale(0.5);
    label.setAlpha(1);

    // Quick zoom-in to readable size
    scene.tweens.add({
      targets: label,
      scaleX: 2.2,
      scaleY: 2.2,
      duration: 200,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!label.active) return;
        // Hold briefly, then float up and fade
        scene.tweens.add({
          targets: label,
          y: label.y - 20,
          alpha: 0,
          duration: 600,
          delay: 300,
          ease: 'Quad.easeIn',
          onComplete: () => label.destroy(),
        });
      },
    });
  }

  /**
   * Clean up resources.
   * @param {boolean} [fromScene]
   */
  destroy(fromScene) {
    if (this._floatTween) {
      this._floatTween.destroy();
      this._floatTween = null;
    }
    if (this._glowTween) {
      this._glowTween.destroy();
      this._glowTween = null;
    }
    if (this._glow) {
      this._glow.destroy();
      this._glow = null;
    }
    super.destroy(fromScene);
  }
}
